import {
  Button,
  DatePicker,
  Empty,
  Radio,
  Select,
  useBreadcrumbRoutes,
  Wrapper,
  EchartsWithTheme,
  useTheme,
} from '@maxtropy/components';
import styles from './index.module.scss';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useRequest } from 'ahooks';
import {
  apiV2MicrogridOverviewIncomeAnalysePost,
  apiV2MicrogridResolvedGetMicrogridConfigListPost,
} from '@maxtropy/intelligent-dispath-apis-v2';
import tittleDecoration from '../imgs/tittleDecoration.png';

import { useLocation } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { Spin } from 'antd';
import { getChartOption } from './chart';
import ConfigModal from './ConfigModal';
import { ResolutionEnum, ResolutionEnumDisplay } from '../type';
import { useHasPermission } from '@/utils/utils';
import { PermissionsType } from '@/common/permissionsConst';
import { EchartsWithThemeRef } from '@maxtropy/components/es/components/EchartsWithTheme';

const IncomeCompare = () => {
  const { search } = useLocation();
  const urlSearchParams = new URLSearchParams(search);
  const defaultMicrogridId = urlSearchParams.get('microgridId'); // 结构id
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const theme = useTheme();
  const [microgridId, setMicrogridId] = useState<number>(); // 微电网
  const [resolution, setResolution] = useState<ResolutionEnum>(ResolutionEnum.DAY);
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [visible, setVisible] = useState<boolean>(false);
  const hasPermission = useHasPermission(PermissionsType.B_POLICYCONFIGURATIONBUTTONWASNOTEXCUTED);

  // 查询微电网
  const { data: microgridsList } = useRequest(() =>
    apiV2MicrogridResolvedGetMicrogridConfigListPost({}).then(res => {
      return res?.list ?? [];
    })
  );

  useEffect(() => {
    if (defaultMicrogridId) {
      setMicrogridId(Number(defaultMicrogridId));
    } else {
      if (!microgridsList || microgridsList.length === 0) {
        setMicrogridId(undefined);
      } else {
        setMicrogridId(microgridsList?.[0].configId);
      }
    }
  }, [defaultMicrogridId, microgridsList]);

  // 收益分析报表
  const { data, loading } = useRequest(
    () =>
      apiV2MicrogridOverviewIncomeAnalysePost({
        configId: microgridId,
        time: resolution === ResolutionEnum.DAY ? date.startOf('day').valueOf() : date.startOf('month').valueOf(),
        type: resolution,
      }).then(res => {
        return res;
      }),
    {
      ready: !!microgridId && !!date,
      refreshDeps: [microgridId, date, resolution],
    }
  );

  // 图表
  const chartOption = useMemo(() => getChartOption(resolution, data), [data, resolution]);

  const chartRef = useRef<EchartsWithThemeRef>();
  useEffect(() => {
    const handleResize = () => {
      if (chartRef.current) {
        chartRef.current.getInstance()?.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? [])]}>
      <div className={styles.contentInner}>
        <div className={styles.filter_wrapper}>
          <div className={styles.select_box}>
            <span className={styles.label}>微电网：</span>
            <Select
              placeholder="请选择"
              style={{ width: 240 }}
              options={(microgridsList ?? []).map(item => ({
                label: item.displayName,
                value: item.configId,
              }))}
              value={microgridId}
              onChange={val => setMicrogridId(val)}
              showSearch
              optionFilterProp="label"
            />
          </div>
          {hasPermission && (
            <Button
              type="primary"
              onClick={() => {
                setVisible(true);
              }}
            >
              未执行策略配置
            </Button>
          )}
        </div>
        <div className={styles.container}>
          <div className={styles.headerWrapper}>
            <img src={tittleDecoration} alt="pic" className={styles.titleBorder} />
            <span className={styles.title}>收益分析</span>
          </div>
          <div className={styles.content_box}>
            <div className={styles.select_box}>
              <Radio.Group
                buttonStyle="solid"
                value={resolution}
                onChange={e => {
                  setResolution(e.target.value);
                }}
              >
                <Radio.Button value={ResolutionEnum.DAY}>{ResolutionEnumDisplay[ResolutionEnum.DAY]}</Radio.Button>
                <Radio.Button value={ResolutionEnum.MONTH}>{ResolutionEnumDisplay[ResolutionEnum.MONTH]}</Radio.Button>
              </Radio.Group>
              <DatePicker
                style={{ width: 240 }}
                allowClear={false}
                picker={resolution === ResolutionEnum.DAY ? 'date' : 'month'}
                value={date}
                onChange={date => setDate(date)}
                disabledDate={current => {
                  return current > dayjs();
                }}
              />
            </div>
            <div className={styles.chart_box}>
              <EchartsWithTheme
                theme={theme}
                loading={loading}
                empty={{
                  isShowEmpty: data?.list?.length === 0,
                  emptyNode: <Empty className={styles.empty_box} />,
                }}
                ref={d => {
                  if (d) {
                    chartRef.current = d;
                  }
                }}
                style={{ height: 580, width: '100%' }}
                option={chartOption}
              />
            </div>
          </div>
        </div>
        {visible && <ConfigModal microgridId={microgridId} visible={visible} setVisible={setVisible}></ConfigModal>}
      </div>
    </Wrapper>
  );
};

export default IncomeCompare;
